import { Language } from '@/utils/i18n/language.interface';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable, signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, type Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import type { BuildingUserAccess } from '../../interfaces/Access.interface';
import type { RankedStandardList } from '../../interfaces/CombinedList.interface';
import type { PasswordUpdate, ProfileRole } from '../../interfaces/Profile.interface';
import type { UserCore, UserId } from '../../interfaces/User.interface';
import type { AppState } from '../../store/app.state';
import { ProfileStoreActions, ProfileStoreSelectors } from '../../store/profile';
import { BrandingProvider } from '../branding/branding.provider';
import { ErrorHandlingProvider } from '../error-handling/error-handling.provider';
import { APIError } from '../errors.constants';
import { InformationProvider } from '../information/information.provider';
import { SESSION_STORAGE_KEYS } from '../local-storage.contants';
import { ACCESS_INTERNAL_MODULE, ACCESS_PROJECT_ADMINISTRATION } from '../rights.constants';
import { StoreProvider } from '../store.provider';
import { AdvizeoCoreProfileProvider } from './advizeo-core-profile.provider';

@Injectable({
    providedIn: 'root'
})
export class ProfileProvider {
    private _advizeoCoreProfileProvider = inject(AdvizeoCoreProfileProvider);
    private _store = inject<Store<AppState>>(Store);
    private _translateService = inject(TranslateService);
    private _informationProvider = inject(InformationProvider);
    private _storeProvider = inject(StoreProvider);
    private _brandingProvider = inject(BrandingProvider);
    private _errorHandlingProvider = inject(ErrorHandlingProvider);

    static USER_ACCESSES: RankedStandardList[] = [
        { code: 'admin', label: 'USER_ACCESS.ADMIN_OPTION', rank: 3, iconUrl: null },
        { code: 'read', label: 'USER_ACCESS.READ_OPTION', rank: 1, iconUrl: null },
        { code: 'write', label: 'USER_ACCESS.WRITE_OPTION', rank: 2, iconUrl: null },
        { code: 'partial', label: 'USER_ACCESS.PARTIAL_OPTION', rank: 0, iconUrl: null }
    ];

    private _profileStoreStateSignal = signal<string[] | null>(null);

    private isCurrentUserLoaded$(): Observable<boolean> {
        return this._store.pipe(select(ProfileStoreSelectors.selectIsCurrentUserLoaded));
    }

    getUser$(): Observable<UserCore> {
        return this._store.pipe(select(ProfileStoreSelectors.selectCurrentUser), filter(isNotNullish));
    }

    getDefinedUser$(): Observable<UserCore | null> {
        return this.isCurrentUserLoaded$().pipe(switchMap(isCurrentUserLoaded => (isCurrentUserLoaded ? this.getUser$() : of(null))));
    }

    getUser(): UserCore {
        return this._storeProvider.getStoreInstantValue(ProfileStoreSelectors.selectCurrentUser)!;
    }

    getUserId$(): Observable<UserId> {
        return this._store.pipe(select(ProfileStoreSelectors.selectCurrentUserId), filter(isNotNullish));
    }

    getUserId(): UserId {
        return this._storeProvider.getStoreInstantValue(ProfileStoreSelectors.selectCurrentUserId)!;
    }

    getUserProfileRoles$(): Observable<ProfileRole[]> {
        return this._advizeoCoreProfileProvider.getUserProfileRoles$();
    }

    fetchCurrentUser(userId: UserId): Promise<UserCore> {
        this._store.dispatch(new ProfileStoreActions.FetchCurrentUser());
        return firstValueFrom(this._advizeoCoreProfileProvider.fetchCurrentUser(userId))
            .then(user => {
                if (!user) {
                    throw Error('User does not exist');
                }
                this._store.dispatch(new ProfileStoreActions.FetchCurrentUserSucceeded({ user }));
                return user;
            })
            .catch(error => {
                console.error('Error while fetching current user::', error);
                this._store.dispatch(new ProfileStoreActions.FetchCurrentUserFailed());
                throw error;
            });
    }

    getUserName(): string {
        return this._storeProvider.getStoreInstantValue(ProfileStoreSelectors.selectCurrentUserName)!;
    }

    getUserEmail(): string {
        return this._storeProvider.getStoreInstantValue(ProfileStoreSelectors.selectCurrentUserEmail)!;
    }

    getRights(): string[] {
        return this._storeProvider.getStoreInstantValue(ProfileStoreSelectors.selectRights);
    }

    getRights$(): Observable<string[]> {
        return this._store.pipe(select(ProfileStoreSelectors.selectRights));
    }

    getUserProfilePictureUrl$(): Observable<string> {
        return this._store.pipe(
            select(ProfileStoreSelectors.selectCurrentUserPictureUrl),
            map(storedPictureUrl => {
                return storedPictureUrl ? storedPictureUrl : 'assets/icons/default_user.png';
            })
        );
    }

    getUserLanguage$(): Observable<Language> {
        return this._store.pipe(
            select(ProfileStoreSelectors.selectCurrentUserLanguage),
            map(lang => {
                if (isNotNullish(lang)) return lang;
                const sessionLanguage = sessionStorage.getItem(SESSION_STORAGE_KEYS.CURRENT_LANGUAGE) as Language | null;
                if (isNotNullish(sessionLanguage)) return sessionLanguage;
                const navigatorLanguage = navigator.language.slice(0, 2);
                if ((Object.values(Language) as string[]).includes(navigatorLanguage)) {
                    return navigatorLanguage as Language;
                } else {
                    return Language.EN;
                }
            })
        );
    }

    getUserLanguage(): Language {
        const storedLanguage = this._storeProvider.getStoreInstantValue(ProfileStoreSelectors.selectCurrentUserLanguage);
        return storedLanguage ? storedLanguage : Language.FR;
    }

    hasRight$(right: string): Observable<boolean> {
        return this._store.pipe(
            select(ProfileStoreSelectors.selectRights),
            map(rights => rights.includes(right))
        );
    }

    /**
     * Waits for rights initialization before resolving for a definitive answer
     */
    hasRight(right: string): Promise<boolean> {
        return firstValueFrom(
            this._store.pipe(
                select(ProfileStoreSelectors.selectAreRightsLoaded),
                filter(isLoaded => isLoaded),
                switchMap(() => this.hasRight$(right))
            )
        );
    }

    hasRightR(right: string): boolean {
        return this._profileStoreStateSignal()?.includes(right) ?? false;
    }

    isProjectAdmin$(): Observable<boolean> {
        return this.hasRight$(ACCESS_PROJECT_ADMINISTRATION);
    }

    isProjectAdminR(): boolean {
        return this.hasRightR(ACCESS_PROJECT_ADMINISTRATION);
    }

    hasInternalModuleAccess(): Promise<boolean> {
        return this.hasRight(ACCESS_INTERNAL_MODULE);
    }

    hasInternalModuleAccess$(): Observable<boolean> {
        return this.hasRight$(ACCESS_INTERNAL_MODULE);
    }

    hasInternalModuleAccessR(): boolean {
        return this._profileStoreStateSignal()?.includes(ACCESS_INTERNAL_MODULE) ?? false;
    }

    updateProfileUser(userUpdate: Partial<UserCore>, userId?: UserId): Promise<void> {
        this._store.dispatch(new ProfileStoreActions.SetIsPending(true));
        const updatePromises = Object.keys(userUpdate).map(propName =>
            firstValueFrom(
                this._advizeoCoreProfileProvider.updateProfileField(
                    userId ? userId : this.getUserId(),
                    propName,
                    userUpdate[propName as keyof typeof userUpdate]
                )
            )
                .then(() => {
                    this._store.dispatch(
                        new ProfileStoreActions.UpdateCurrentUser({
                            userUpdate: { [propName]: userUpdate[propName as keyof typeof userUpdate] }
                        })
                    );
                })
                .catch(error => {
                    console.error(`Error while updating currentUser ${propName} property::`, error);
                    throw error;
                })
        );
        return Promise.all(updatePromises).then(() => {
            this._store.dispatch(new ProfileStoreActions.SetIsPending(false));
        });
    }

    updateProfilePicture(file: FormData): Promise<void> {
        this._store.dispatch(new ProfileStoreActions.SetIsPending(true));
        return firstValueFrom(this._advizeoCoreProfileProvider.updatePicture(this.getUserId(), file))
            .then((pictureUrl: string) => {
                this._store.dispatch(new ProfileStoreActions.UpdateCurrentUser({ userUpdate: { pictureUrl } }));
                this._store.dispatch(new ProfileStoreActions.SetIsPending(false));
            })
            .catch(error => {
                console.error('Error while updating profile picture::', error);
                this._store.dispatch(new ProfileStoreActions.SetIsPending(false));
                throw error;
            });
    }

    updatePassword(userEmail: string, passwords: PasswordUpdate): Promise<void> {
        return firstValueFrom(this._advizeoCoreProfileProvider.updatePassword(userEmail, passwords))
            .then(() => {
                this._informationProvider.showToast('MESSAGES.PROFILE.UPDATE.SUCCESS', 'success');
            })
            .catch(error => {
                console.error('Error while updating password::', error);
                this._informationProvider.showToast('MESSAGES.PROFILE.UPDATE.FAILURE', 'error');
                throw error;
            });
    }

    updatePasswordUsingToken(userEmail: string, token: string, password: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this._advizeoCoreProfileProvider.updatePasswordUsingToken(userEmail, token, password).subscribe({
                next: () => {
                    this._informationProvider.showToast('MESSAGES.PROFILE.UPDATE.SUCCESS', 'success');
                    resolve();
                },
                error: () => {
                    this._informationProvider.showToast('MESSAGES.PROFILE.UPDATE.FAILURE', 'error');
                    reject();
                }
            });
        });
    }

    fetchRights(): Promise<string[]> {
        return firstValueFrom(this._advizeoCoreProfileProvider.fetchRights())
            .then((rights: string[]) => {
                this._store.dispatch(new ProfileStoreActions.SetRights({ rights }));
                this._profileStoreStateSignal.set(rights);
                return rights;
            })
            .catch(error => {
                console.error('Error while fetching rights', error);
                throw error;
            });
    }

    // TODO use response (error cases)
    updateUsersRight(usersBuildingsAccessesCombinations: BuildingUserAccess[]): Promise<any> {
        const branding = this._brandingProvider.getPublicBrandingRootUri();
        return firstValueFrom(this._advizeoCoreProfileProvider.updateRight(usersBuildingsAccessesCombinations, branding)).catch(err => {
            console.error('Failed to update users rights', err);
            this._informationProvider.showToast('MESSAGES.PROFILE.UPDATE.FAILURE', 'error');
        });
    }

    deleteUserAccesses(accesses: BuildingUserAccess[]): Promise<BuildingUserAccess[] | void> {
        return new Promise((resolve, reject) => {
            this._advizeoCoreProfileProvider.deleteUserAccesses(accesses).subscribe({
                next: (res: any) => {
                    // Some rights could not be remove
                    if (res) {
                        const rightsToDeleteFilter = accesses.filter(right => {
                            const isInError = res.find((rightInError: any) => {
                                return (
                                    rightInError.data.userId === right.userId &&
                                    rightInError.data.buildingId === right.buildingId &&
                                    rightInError.data.access === right.access
                                );
                            });
                            return !isInError ? true : false;
                        });

                        resolve(rightsToDeleteFilter);
                    } else {
                        resolve();
                    }
                },
                error: error => {
                    reject(error);
                }
            });
        });
    }

    deleteUserAccess(access: BuildingUserAccess) {
        return this.deleteUserAccesses([access]).then(deletedRights => {
            if (!deletedRights) {
                return;
            }
            throw {
                notDeletedAccesses: [access]
            };
        });
    }

    hasFeatureAccess(code: string): boolean {
        if (this.getRights().includes(code)) {
            return true;
        } else {
            return false;
        }
    }

    addNewUser(user: any) {
        if (!user['language']) user['language'] = this._translateService.getCurrentLang();
        return firstValueFrom(this._advizeoCoreProfileProvider.addNewUser(user))
            .then(() => {
                this._informationProvider.showToast('PAGES.INVITATION.REGISTER_SUCCESS', 'success', 60000);
            })
            .catch(err => {
                const errorCode = this._errorHandlingProvider.getErrorCodeFromApiResponse(err);
                if (errorCode === APIError.ALREADY_CREATED) {
                    this._informationProvider.showToast('MESSAGE_ERROR.ALREADY_CREATED_ACCOUNT', 'error');
                } else if (errorCode === APIError.UNEXISTING_INVITATION) {
                    this._informationProvider.showToast('MESSAGE_ERROR.UNEXISTING_INVITATION', 'error');
                } else {
                    this._informationProvider.showToast('PAGES.INVITATION.REGISTER_FAILED', 'error');
                }
                throw err;
            });
    }

    acceptInvitation(accountId: string, token: string) {
        return new Promise((resolve, reject) => {
            this._advizeoCoreProfileProvider.acceptInvitation(accountId, token).subscribe({
                next: res => {
                    if (res && res.status !== 200 && res.status !== 204) {
                        this._informationProvider.showToast('PAGES.INVITATION.REGISTER_FAILED', 'error');
                    } else {
                        this._informationProvider.showToast('PAGES.INVITATION.REGISTER_SUCCESS', 'success');
                    }
                    resolve(res);
                },
                error: err => {
                    const errorCode = this._errorHandlingProvider.getErrorCodeFromApiResponse(err);
                    if (errorCode === APIError.ALREADY_ACCEPTED) {
                        this._informationProvider.showToast('MESSAGE_ERROR.ALREADY_ACCEPTED_INVITATION', 'error');
                        resolve(err);
                    } else {
                        this._informationProvider.showToast('PAGES.INVITATION.REGISTER_FAILED', 'error');
                        reject(err);
                    }
                }
            });
        });
    }

    isProfileLoading$(): Observable<boolean> {
        return this._store.pipe(select(ProfileStoreSelectors.selectIsPending));
    }

    areRightsLoaded$(): Observable<boolean> {
        return this._store.pipe(select(ProfileStoreSelectors.selectAreRightsLoaded));
    }
}
